import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { User, MapPin, Package, LogOut, ArrowRight, Sparkles, MessageSquare, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import * as profileApi from "../api/profileApi";
import "./Account.css";

export default function Account() {
  const { logout, userId } = useAuth();
  const { t, isRTL } = useLanguage();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    profileApi
      .getProfile(userId)
      .then((data) => {
        if (!cancelled && data) setProfile(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const fullName = profile ? [profile.firstName, profile.lastName].filter(Boolean).join(" ") : "";

  return (
    <div className="account-page">
      <div className="container">
        {/* Account Header & Luxury Greeting */}
        <div className="account-page__header">
          <div className="account-avatar-emblem">
            <span className="account-avatar-initials">
              {fullName ? fullName.charAt(0).toUpperCase() : <User size={24} />}
            </span>
          </div>

          <div className="account-member-badge">
            <Sparkles size={13} />
            <span>{t("account.memberBadge")}</span>
          </div>

          <h1 className="account-welcome-title">
            {t("account.welcome")}
            {fullName ? `, ${fullName}` : ""}
          </h1>

          <p className="account-welcome-sub">{t("account.title")}</p>
        </div>

        {/* Account Dashboard Grid */}
        <div className="account-grid">
          {/* Tile 1: Profile */}
          <Link to="/account/profile" className="account-tile">
            <div className="account-tile__icon-box">
              <User size={22} strokeWidth={1.5} />
            </div>
            <div className="account-tile__body">
              <h3>{t("account.profileTitle")}</h3>
              <p>{t("account.profileDesc")}</p>
            </div>
            <span className="account-tile__arrow">
              <span>{t("account.profileAction")}</span>
              <ArrowRight size={14} className="rtl-flip" />
            </span>
          </Link>

          {/* Tile 2: Orders */}
          <Link to="/account/orders" className="account-tile">
            <div className="account-tile__icon-box">
              <Package size={22} strokeWidth={1.5} />
            </div>
            <div className="account-tile__body">
              <h3>{t("account.ordersTitle")}</h3>
              <p>{t("account.ordersDesc")}</p>
            </div>
            <span className="account-tile__arrow">
              <span>{t("account.ordersAction")}</span>
              <ArrowRight size={14} className="rtl-flip" />
            </span>
          </Link>

          {/* Tile 3: Addresses */}
          <Link to="/account/addresses" className="account-tile">
            <div className="account-tile__icon-box">
              <MapPin size={22} strokeWidth={1.5} />
            </div>
            <div className="account-tile__body">
              <h3>{t("account.addressesTitle")}</h3>
              <p>{t("account.addressesDesc")}</p>
            </div>
            <span className="account-tile__arrow">
              <span>{t("account.addressesAction")}</span>
              <ArrowRight size={14} className="rtl-flip" />
            </span>
          </Link>

          {/* Tile 4: Concierge & Customer Care */}
          <Link to="/contact" className="account-tile">
            <div className="account-tile__icon-box">
              <MessageSquare size={22} strokeWidth={1.5} />
            </div>
            <div className="account-tile__body">
              <h3>{t("account.supportTitle")}</h3>
              <p>{t("account.supportDesc")}</p>
            </div>
            <span className="account-tile__arrow">
              <span>{t("account.supportAction")}</span>
              <ArrowRight size={14} className="rtl-flip" />
            </span>
          </Link>

          {/* Tile 5: Sign Out */}
          <button
            type="button"
            className="account-tile account-tile--danger"
            onClick={() => logout()}
          >
            <div className="account-tile__icon-box account-tile__icon-box--danger">
              <LogOut size={22} strokeWidth={1.5} className="rtl-flip" />
            </div>
            <div className="account-tile__body">
              <h3>{t("account.signOutTitle")}</h3>
              <p>{t("account.signOutDesc")}</p>
            </div>
            <span className="account-tile__arrow">
              <span>{t("account.signOutAction")}</span>
              <ArrowRight size={14} className="rtl-flip" />
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
